/**
 * PLANNING D'UNE PERSONNE (?personne=) — la lecture du paramètre, le bandeau
 * et l'adresse, en calculs purs (testés sans navigateur :
 * tests/planning-personne.test.ts).
 *
 * Aucun import, à dessein : les modèles de menu recopient l'adresse au lieu
 * de dépendre d'un module de page, et ce fichier n'en dépend pas non plus.
 *
 * Le filtre est EXACT, sur person.id (côté RH), jamais sur le nom : `?q=`
 * reste la recherche par sous-chaîne (« Léa » montre aussi « Léane »).
 * Il ne s'applique qu'à l'effectif déjà servi par le serveur, déjà restreint
 * au périmètre de l'acteur : il ne montre jamais rien de plus que la grille,
 * et l'identifiant n'est envoyé à aucune requête.
 */

/** Le motif de la route de la fiche salarié (App.tsx, `personDetail`). */
export const ID_PERSONNE = /^[0-9a-f-]{8,40}$/

/**
 * `?personne=` tel que la page l'accepte : espaces retirés, minuscules.
 * Une valeur hors motif vaut '' : la page l'ignore, et l'adresse est
 * réécrite sans elle au premier rendu.
 */
export function lirePersonneParam(brut: string | null | undefined): string {
  if (typeof brut !== 'string') return ''
  const v = brut.trim().toLowerCase()
  return ID_PERSONNE.test(v) ? v : ''
}

/**
 * Ce que dit le bandeau au-dessus de la grille :
 *   · aucun      : pas de filtre, pas de bandeau ;
 *   · chargement : l'effectif n'est pas encore arrivé ;
 *   · trouve     : la personne est dans l'effectif servi, avec SON nom ;
 *   · absent     : hors périmètre, sortie, archivée ou candidate. La phrase
 *                  reste neutre : elle ne confirme pas que la personne existe.
 */
export type BandeauPersonne =
  | { etat: 'aucun' }
  | { etat: 'chargement' }
  | { etat: 'trouve'; nom: string }
  | { etat: 'absent' }

/** Le nom vient TOUJOURS de l'effectif servi, jamais de l'adresse. */
export function bandeauPersonne(
  personId: string,
  people: readonly { person_id: string; display_name: string }[] | null,
): BandeauPersonne {
  if (!personId) return { etat: 'aucun' }
  if (people === null) return { etat: 'chargement' }
  const p = people.find((x) => x.person_id === personId)
  return p ? { etat: 'trouve', nom: p.display_name } : { etat: 'absent' }
}

/**
 * L'adresse canonique du planning d'une personne. App.tsx remonte la page à
 * chaque navigation (key={navigations}) : un lien ou un favori vers une autre
 * personne la relit. « Aller à un salarié… », dans la page, change de personne
 * sur place, sans navigation.
 */
export const CHEMIN_PLANNING = '/equipe/temps/planning'
export const cheminPlanningPersonne = (personId: string): string =>
  `${CHEMIN_PLANNING}?${new URLSearchParams({ personne: personId }).toString()}`
